import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '../stores/auth';

// Tiempo de inactividad en minutos
const INACTIVITY_TIMEOUT = 30;
// Tiempo de la alerta en segundos
const ALERT_COUNTDOWN = 30;

let inactivityTimer: NodeJS.Timeout | null = null;
let countdownTimer: NodeJS.Timeout | null = null;

const showInactivityAlert = ref(false);
const countdown = ref(ALERT_COUNTDOWN);

export function useAuth() {
  const router = useRouter();
  const route = useRoute();
  const auth = useAuthStore();
  const error = ref<string>('');

  const isAuthenticated = computed(() => auth.user !== null);
  const currentUser = computed(() => auth.user);
  const isLoading = computed(() => auth.isLoading);
  const userRole = computed(() => auth.userRole);
  const isAdmin = computed(() => auth.isAdmin);

  const resetInactivityTimer = () => {
    // Solo resetear si no está mostrando la alerta
    if (!showInactivityAlert.value) {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
        inactivityTimer = null;
      }
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
      
      countdown.value = ALERT_COUNTDOWN;

      // Solo configurar el timer si el usuario está autenticado
      if (isAuthenticated.value) {
        // Calculamos el tiempo total en milisegundos
        const totalTimeout = INACTIVITY_TIMEOUT * 60 * 1000;
        // Restamos los 30 segundos de la alerta
        const timeUntilAlert = totalTimeout - (ALERT_COUNTDOWN * 1000);
        
        inactivityTimer = setTimeout(() => {
          startLogoutCountdown();
        }, timeUntilAlert);
      }
    }
  };

  const startLogoutCountdown = () => {
    showInactivityAlert.value = true;
    countdown.value = ALERT_COUNTDOWN;
    
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }
    
    countdownTimer = setInterval(() => {
      countdown.value--;
      if (countdown.value <= 0) {
        if (countdownTimer) {
          clearInterval(countdownTimer);
          countdownTimer = null;
        }
        handleLogout();
      }
    }, 1000);
  };

  const cancelLogoutCountdown = () => {
    showInactivityAlert.value = false;
    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }
    countdown.value = ALERT_COUNTDOWN;
    resetInactivityTimer();
  };

  const handleLogout = async () => {
    try {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
        inactivityTimer = null;
      }
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
      showInactivityAlert.value = false;
      countdown.value = ALERT_COUNTDOWN;
      
      // Primero limpiamos la detección de inactividad
      cleanupInactivityDetection();
      
      // Luego cerramos la sesión
      await auth.logout();
      
      // Finalmente redirigimos
      router.push('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Intentar cerrar sesión de todas formas
      router.push('/login');
    }
  };

  const logout = async () => {
    await handleLogout();
  };

  // Configurar event listeners para resetear el timer
  const setupInactivityDetection = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer);
    });

    // Configurar timer inicial
    resetInactivityTimer();
  };

  // Limpiar event listeners
  const cleanupInactivityDetection = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.removeEventListener(event, resetInactivityTimer);
    });

    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }
    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      error.value = '';
      const result = await auth.login(email, password);
      
      if (result.success) {
        setupInactivityDetection();
        resetInactivityTimer();
        // Redirigir a la ruta original si existe, sino al dashboard
        const redirectPath = route.query.redirect?.toString() || '/dashboard/tracking-request';
        await router.push(redirectPath);
      } else {
        error.value = result.error || 'Error al iniciar sesión';
      }
      
      return result;
    } catch (e) {
      error.value = 'Error al iniciar sesión';
      throw e;
    }
  };

  const loginWithProvider = async (provider: 'google' | 'microsoft') => {
    try {
      error.value = '';
      const result = await auth.loginWithProvider(provider);
      
      if (result.success) {
        setupInactivityDetection();
        resetInactivityTimer();
        // Redirigir a la ruta original si existe, sino al dashboard
        const redirectPath = route.query.redirect?.toString() || '/dashboard/tracking-request';
        await router.push(redirectPath);
      } else {
        error.value = result.error || 'Error al iniciar sesión';
      }
      
      return result;
    } catch (e) {
      error.value = `Error al iniciar sesión con ${provider}`;
      throw e;
    }
  };

  const checkAuth = async () => {
    try {
      const isAuth = await auth.checkAuth();
      if (isAuth) {
        setupInactivityDetection();
        resetInactivityTimer();
      }
      return isAuth;
    } catch (e) {
      console.error('Error checking auth:', e);
      return false;
    }
  };

  // Configurar el temporizador de inactividad cuando el componente se monta
  onMounted(() => {
    if (isAuthenticated.value) {
      setupInactivityDetection();
      resetInactivityTimer();
    }
  });

  // Limpiar el temporizador cuando el componente se desmonta
  onUnmounted(() => {
    cleanupInactivityDetection();
  });

  return {
    isAuthenticated,
    currentUser,
    isLoading,
    userRole,
    isAdmin,
    error,
    login,
    loginWithProvider,
    logout,
    checkAuth,
    showInactivityAlert,
    countdown,
    cancelLogoutCountdown,
    handleLogout
  };
} 